import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Slider } from '@/components/ui/slider'
import { useLabelStore } from '@/stores/labelStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useElementsStore } from '@/stores/elementsStore'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DPI } from '@/lib/utils/units'
import { LabelMargins, MediaType, PrinterConnectionType } from '@/types'
import { Plus, Trash2, Wifi, Globe } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Visual media type selector
function MediaTypeSelector({
  mediaType,
  onChange,
}: {
  mediaType: MediaType
  onChange: (m: MediaType) => void
}) {
  const types: { value: MediaType; label: string; description: string }[] = [
    { value: 'gap', label: 'Gap/Web', description: 'Labels with gaps between' },
    { value: 'continuous', label: 'Continuous', description: 'No gaps, continuous roll' },
    { value: 'mark', label: 'Black Mark', description: 'Black mark sensing' },
  ]

  return (
    <div className="grid grid-cols-3 gap-2">
      {types.map((type) => (
        <button
          key={type.value}
          type="button"
          onClick={() => onChange(type.value)}
          className={cn(
            "flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all",
            mediaType === type.value
              ? "border-primary bg-primary/5"
              : "border-muted hover:border-muted-foreground/30"
          )}
        >
          {/* Visual representation */}
          <div className="flex gap-0.5 items-end h-8">
            {type.value === 'gap' && (
              <>
                <div className="w-4 h-6 bg-white border border-gray-300 rounded-sm" />
                <div className="w-1 h-6" /> {/* Gap */}
                <div className="w-4 h-6 bg-white border border-gray-300 rounded-sm" />
                <div className="w-1 h-6" /> {/* Gap */}
                <div className="w-4 h-6 bg-white border border-gray-300 rounded-sm" />
              </>
            )}
            {type.value === 'continuous' && (
              <div className="w-14 h-6 bg-white border border-gray-300 rounded-sm" />
            )}
            {type.value === 'mark' && (
              <>
                <div className="flex flex-col">
                  <div className="w-4 h-5 bg-white border border-gray-300 rounded-t-sm" />
                  <div className="w-4 h-1 bg-black" />
                </div>
                <div className="w-0.5 h-6" />
                <div className="flex flex-col">
                  <div className="w-4 h-5 bg-white border border-gray-300 rounded-t-sm" />
                  <div className="w-4 h-1 bg-black" />
                </div>
              </>
            )}
          </div>
          <span className={cn(
            "text-[10px] text-center",
            mediaType === type.value ? "text-primary font-medium" : "text-muted-foreground"
          )}>
            {type.label}
          </span>
        </button>
      ))}
    </div>
  )
}

// Visual label preview component
// Concept: width/height = printable area (útil), margins = external gap/unprintable area AROUND the label
function LabelPreview({
  width,
  height,
  margins,
}: {
  width: number
  height: number
  margins: LabelMargins
}) {
  // Total physical size = printable area + margins
  const totalWidth = width + margins.left + margins.right
  const totalHeight = height + margins.top + margins.bottom

  // Scale to fit in preview area (max 140px)
  const maxSize = 140
  const scale = Math.min(maxSize / totalWidth, maxSize / totalHeight, 2)

  const displayTotalWidth = totalWidth * scale
  const displayTotalHeight = totalHeight * scale
  const displayWidth = width * scale
  const displayHeight = height * scale

  const marginLeft = margins.left * scale
  const marginRight = margins.right * scale
  const marginTop = margins.top * scale
  const marginBottom = margins.bottom * scale

  const hasMargins = margins.left > 0 || margins.right > 0 || margins.top > 0 || margins.bottom > 0

  // Hatched pattern style for margin areas
  const hatchedStyle = {
    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(239,68,68,0.2) 2px, rgba(239,68,68,0.2) 4px)',
    backgroundColor: 'rgba(239,68,68,0.05)',
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-xs text-muted-foreground">Preview</div>
      {/* Total physical area (including margins) */}
      <div
        className="relative border border-dashed border-gray-300"
        style={{
          width: displayTotalWidth,
          height: displayTotalHeight,
        }}
      >
        {/* Margin areas (hatched pattern) - external to printable area */}
        {margins.top > 0 && (
          <div
            className="absolute left-0 right-0 top-0"
            style={{ height: marginTop, ...hatchedStyle }}
          />
        )}
        {margins.bottom > 0 && (
          <div
            className="absolute left-0 right-0 bottom-0"
            style={{ height: marginBottom, ...hatchedStyle }}
          />
        )}
        {margins.left > 0 && (
          <div
            className="absolute left-0 top-0 bottom-0"
            style={{ width: marginLeft, ...hatchedStyle }}
          />
        )}
        {margins.right > 0 && (
          <div
            className="absolute right-0 top-0 bottom-0"
            style={{ width: marginRight, ...hatchedStyle }}
          />
        )}

        {/* Printable area (útil) - white, in the center */}
        <div
          className="absolute bg-white border-2 border-gray-400"
          style={{
            left: marginLeft,
            top: marginTop,
            width: displayWidth,
            height: displayHeight,
          }}
        />

        {/* Dimension labels for printable area */}
        {marginTop >= 12 && (
          <div
            className="absolute text-[10px] text-muted-foreground"
            style={{
              left: marginLeft + displayWidth / 2,
              top: marginTop - 12,
              transform: 'translateX(-50%)',
            }}
          >
            {width}mm
          </div>
        )}
        {marginLeft >= 12 && (
          <div
            className="absolute text-[10px] text-muted-foreground -rotate-90"
            style={{
              left: marginLeft - 12,
              top: marginTop + displayHeight / 2,
              transform: 'translateY(-50%)',
            }}
          >
            {height}mm
          </div>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground text-center">
        {hasMargins ? (
          <>Total: {totalWidth.toFixed(1)}×{totalHeight.toFixed(1)}mm</>
        ) : (
          <>{width}×{height}mm</>
        )}
      </div>
    </div>
  )
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const {
    label,
    setLabel,
    setWidth,
    setHeight,
    setDPI,
    setName,
    setMargins,
    setPrintSpeed,
    setDarkness,
    setMediaType,
    setGapLength,
  } = useLabelStore()
  const { gridSize, setGridSize, variableData, setVariableData, printer, setPrinter } = useSettingsStore()
  const { elements } = useElementsStore()

  const [newVarName, setNewVarName] = useState('')
  const [newVarValue, setNewVarValue] = useState('')

  // Find all variable names used in elements
  const usedVariables = elements
    .filter((el) => {
      if ('isVariable' in el && 'variableName' in el) {
        return (el as any).isVariable && (el as any).variableName
      }
      return false
    })
    .map((el) => (el as any).variableName as string)
    .filter((name, index, self) => self.indexOf(name) === index)

  const handleAddVariable = () => {
    if (newVarName.trim()) {
      setVariableData({
        ...variableData,
        [newVarName.trim()]: newVarValue,
      })
      setNewVarName('')
      setNewVarValue('')
    }
  }

  const handleRemoveVariable = (key: string) => {
    const { [key]: _, ...rest } = variableData
    setVariableData(rest)
  }

  const handleUpdateVariable = (key: string, value: string) => {
    setVariableData({
      ...variableData,
      [key]: value,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>
            Configure label properties and variable data.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="label" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="label">Label</TabsTrigger>
            <TabsTrigger value="print">Print</TabsTrigger>
            <TabsTrigger value="printer">Printer</TabsTrigger>
            <TabsTrigger value="grid">Grid</TabsTrigger>
            <TabsTrigger value="variables">Variables</TabsTrigger>
          </TabsList>

          <TabsContent value="label" className="mt-4">
            <div className="grid grid-cols-[1fr_auto] gap-6">
              {/* Form fields */}
              <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                <div className="grid gap-2">
                  <Label htmlFor="label-name">Label Name</Label>
                  <Input
                    id="label-name"
                    value={label.name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="label-width">Width (mm)</Label>
                    <Input
                      id="label-width"
                      type="number"
                      value={label.width}
                      onChange={(e) => setWidth(parseFloat(e.target.value) || 50)}
                      min={10}
                      max={500}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="label-height">Height (mm)</Label>
                    <Input
                      id="label-height"
                      type="number"
                      value={label.height}
                      onChange={(e) => setHeight(parseFloat(e.target.value) || 25)}
                      min={10}
                      max={500}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Printer DPI</Label>
                  <Select
                    value={label.dpi.toString()}
                    onValueChange={(v) => setDPI(parseInt(v) as DPI)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="203">203 DPI (8 dots/mm)</SelectItem>
                      <SelectItem value="300">300 DPI (12 dots/mm)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                <div className="grid gap-2">
                  <Label>Margins / Gap (mm)</Label>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="grid gap-1">
                      <Label htmlFor="margin-left" className="text-xs text-muted-foreground">Left</Label>
                      <Input
                        id="margin-left"
                        type="number"
                        value={label.margins?.left || 0}
                        onChange={(e) => setMargins({ ...label.margins, left: Math.max(0, parseFloat(e.target.value) || 0) })}
                        min={0}
                        step={0.5}
                        className="h-8"
                      />
                    </div>
                    <div className="grid gap-1">
                      <Label htmlFor="margin-right" className="text-xs text-muted-foreground">Right</Label>
                      <Input
                        id="margin-right"
                        type="number"
                        value={label.margins?.right || 0}
                        onChange={(e) => setMargins({ ...label.margins, right: Math.max(0, parseFloat(e.target.value) || 0) })}
                        min={0}
                        step={0.5}
                        className="h-8"
                      />
                    </div>
                    <div className="grid gap-1">
                      <Label htmlFor="margin-top" className="text-xs text-muted-foreground">Top</Label>
                      <Input
                        id="margin-top"
                        type="number"
                        value={label.margins?.top || 0}
                        onChange={(e) => setMargins({ ...label.margins, top: Math.max(0, parseFloat(e.target.value) || 0) })}
                        min={0}
                        step={0.5}
                        className="h-8"
                      />
                    </div>
                    <div className="grid gap-1">
                      <Label htmlFor="margin-bottom" className="text-xs text-muted-foreground">Bottom</Label>
                      <Input
                        id="margin-bottom"
                        type="number"
                        value={label.margins?.bottom || 0}
                        onChange={(e) => setMargins({ ...label.margins, bottom: Math.max(0, parseFloat(e.target.value) || 0) })}
                        min={0}
                        step={0.5}
                        className="h-8"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Gap between labels and unprintable edges.
                  </p>
                </div>
              </div>

              {/* Visual preview */}
              <div className="flex items-start justify-center p-4 bg-muted/30 rounded-lg min-w-[180px]">
                <LabelPreview
                  width={label.width}
                  height={label.height}
                  margins={label.margins || { left: 0, right: 0, top: 0, bottom: 0 }}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="print" className="mt-4">
            <div className="grid grid-cols-[1fr_auto] gap-6">
              {/* Print settings */}
              <div className="space-y-5">
                <div className="grid gap-2">
                  <Label>Media Type</Label>
                  <MediaTypeSelector
                    mediaType={label.printSettings?.mediaType || 'gap'}
                    onChange={setMediaType}
                  />
                  {label.printSettings?.mediaType === 'gap' && (
                    <div className="grid gap-1 mt-2">
                      <Label htmlFor="gap-length" className="text-xs text-muted-foreground">
                        Gap Length (mm)
                      </Label>
                      <Input
                        id="gap-length"
                        type="number"
                        value={label.printSettings?.gapLength || 3}
                        onChange={(e) => setGapLength(parseFloat(e.target.value) || 3)}
                        min={1}
                        max={50}
                        step={0.5}
                        className="h-8 w-24"
                      />
                    </div>
                  )}
                </div>

                <Separator />

                <div className="grid gap-3">
                  <div className="grid gap-2">
                    <div className="flex justify-between items-center">
                      <Label>Print Speed</Label>
                      <span className="text-sm text-muted-foreground">
                        {label.printSettings?.printSpeed || 4} in/sec
                      </span>
                    </div>
                    <Slider
                      value={[label.printSettings?.printSpeed || 4]}
                      onValueChange={([v]) => setPrintSpeed(v)}
                      min={2}
                      max={14}
                      step={1}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Slower speeds improve print quality for detailed labels.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <div className="flex justify-between items-center">
                      <Label>Darkness</Label>
                      <span className="text-sm text-muted-foreground">
                        {label.printSettings?.darkness || 15}
                      </span>
                    </div>
                    <Slider
                      value={[label.printSettings?.darkness || 15]}
                      onValueChange={([v]) => setDarkness(v)}
                      min={0}
                      max={30}
                      step={1}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Higher values produce darker prints, useful for direct thermal.
                    </p>
                  </div>
                </div>
              </div>

              {/* Visual preview */}
              <div className="flex items-start justify-center p-4 bg-muted/30 rounded-lg min-w-[180px]">
                <LabelPreview
                  width={label.width}
                  height={label.height}
                  margins={label.margins || { left: 0, right: 0, top: 0, bottom: 0 }}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="printer" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Configure the printer connection. Choose the connection type based on your setup.
            </p>

            {/* Connection Type Selector */}
            <div className="grid gap-2">
              <Label>Connection Type</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setPrinter({ ...printer, connectionType: 'tcp' })}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all",
                    printer.connectionType === 'tcp'
                      ? "border-primary bg-primary/5"
                      : "border-muted hover:border-muted-foreground/30"
                  )}
                >
                  <Wifi className="h-5 w-5" />
                  <div className="text-center">
                    <div className={cn(
                      "text-sm font-medium",
                      printer.connectionType === 'tcp' ? "text-primary" : ""
                    )}>
                      TCP Direct
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Electron only
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPrinter({ ...printer, connectionType: 'http', port: 3001 })}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all",
                    printer.connectionType === 'http'
                      ? "border-primary bg-primary/5"
                      : "border-muted hover:border-muted-foreground/30"
                  )}
                >
                  <Globe className="h-5 w-5" />
                  <div className="text-center">
                    <div className={cn(
                      "text-sm font-medium",
                      printer.connectionType === 'http' ? "text-primary" : ""
                    )}>
                      HTTP
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Local only
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPrinter({ ...printer, connectionType: 'https', port: 3443 })}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all",
                    printer.connectionType === 'https'
                      ? "border-green-600 bg-green-50"
                      : "border-muted hover:border-muted-foreground/30"
                  )}
                >
                  <Globe className="h-5 w-5" />
                  <div className="text-center">
                    <div className={cn(
                      "text-sm font-medium",
                      printer.connectionType === 'https' ? "text-green-600" : ""
                    )}>
                      HTTPS
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Recommended
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <Separator />

            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="printer-ip">
                  {(printer.connectionType === 'http' || printer.connectionType === 'https') ? 'Simulator IP Address' : 'Printer IP Address'}
                </Label>
                <Input
                  id="printer-ip"
                  value={printer.ip}
                  onChange={(e) => setPrinter({ ...printer, ip: e.target.value })}
                  placeholder={(printer.connectionType === 'http' || printer.connectionType === 'https') ? '127.0.0.1' : '192.168.1.100'}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="printer-port">Port</Label>
                <Input
                  id="printer-port"
                  type="number"
                  value={printer.port}
                  onChange={(e) => setPrinter({ ...printer, port: parseInt(e.target.value) || (printer.connectionType === 'https' ? 3443 : printer.connectionType === 'http' ? 3001 : 9100) })}
                  placeholder={printer.connectionType === 'https' ? '3443' : printer.connectionType === 'http' ? '3001' : '9100'}
                />
              </div>
            </div>

            {printer.connectionType === 'tcp' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                <strong>TCP Direct:</strong> Only works in the Electron desktop app. The browser cannot make TCP connections directly.
              </div>
            )}

            {printer.connectionType === 'http' && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                <strong>HTTP via Simulator:</strong> Uses the NEO ZPL Simulator as a proxy. Only works for local development (HTTP pages).
                <br /><br />
                <code className="text-xs bg-blue-100 px-1 py-0.5 rounded">POST http://{printer.ip || '127.0.0.1'}:{printer.port || 3001}/api/print</code>
              </div>
            )}

            {printer.connectionType === 'https' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                <strong>HTTPS via Simulator:</strong> Uses self-signed certificate - you may need to accept the certificate warning once.
                <br /><br />
                <code className="text-xs bg-green-100 px-1 py-0.5 rounded">POST https://{printer.ip || '127.0.0.1'}:{printer.port || 3443}/api/print</code>
                <br /><br />
                <span className="text-xs">First-time setup: Visit <a href={`https://${printer.ip || '127.0.0.1'}:${printer.port || 3443}/api/health`} target="_blank" className="underline">https://{printer.ip || '127.0.0.1'}:{printer.port || 3443}/api/health</a> and accept the certificate.</span>
              </div>
            )}

            {(printer.connectionType === 'http' || printer.connectionType === 'https') && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                <strong>Firefox only:</strong> Web printing via NEO ZPL Simulator only works in Firefox. Chrome and Edge block requests from public websites to localhost due to Private Network Access restrictions.
              </div>
            )}
          </TabsContent>

          <TabsContent value="grid" className="space-y-4 mt-4">
            <div className="grid gap-2">
              <Label htmlFor="grid-size">Grid Size (mm)</Label>
              <Input
                id="grid-size"
                type="number"
                value={gridSize}
                onChange={(e) => setGridSize(parseFloat(e.target.value) || 5)}
                min={1}
                max={50}
                step={0.5}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Elements will snap to the grid when moved. Toggle the grid visibility
              from the toolbar.
            </p>
          </TabsContent>

          <TabsContent value="variables" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Define values for variable fields used in your label.
            </p>

            {usedVariables.length > 0 && (
              <>
                <Label className="text-xs text-muted-foreground">
                  Variables in use:
                </Label>
                <div className="flex flex-wrap gap-1">
                  {usedVariables.map((name) => (
                    <span
                      key={name}
                      className="px-2 py-1 bg-muted rounded text-xs"
                    >
                      {name}
                    </span>
                  ))}
                </div>
                <Separator />
              </>
            )}

            <ScrollArea className="h-48">
              <div className="space-y-2">
                {Object.entries(variableData).map(([key, value]) => (
                  <div key={key} className="flex gap-2 items-center">
                    <Input value={key} disabled className="flex-1" />
                    <Input
                      value={value}
                      onChange={(e) => handleUpdateVariable(key, e.target.value)}
                      className="flex-1"
                      placeholder="Value"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveVariable(key)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <Separator />

            <div className="flex gap-2">
              <Input
                value={newVarName}
                onChange={(e) => setNewVarName(e.target.value)}
                placeholder="Variable name"
                className="flex-1"
              />
              <Input
                value={newVarValue}
                onChange={(e) => setNewVarValue(e.target.value)}
                placeholder="Value"
                className="flex-1"
              />
              <Button variant="outline" size="icon" onClick={handleAddVariable}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
